'use client';

import { useState, useMemo, useRef, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import type { Product, Collection } from '@/lib/shopify/types';
import { useWishlist } from '@/context/wishlist-context';

interface NewAndPopularProps {
  products: Product[];
  collections: Collection[];
}

function formatPrice(money: { amount: string; currencyCode: string }): string {
  const amount = parseFloat(money.amount);
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: money.currencyCode,
  }).format(amount);
}

export default function NewAndPopular({ products, collections }: NewAndPopularProps) {
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { toggleItem, isInWishlist } = useWishlist();

  const handleFilterClick = useCallback((handle: string, buttonEl: HTMLButtonElement) => {
    setActiveFilter(handle);
    // Smooth scroll the button into view within the horizontal container
    buttonEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, []);

  // Create filter options from collections.
  // The homepage ships a printed-clothing-only catalogue (see
  // src/lib/homepage-catalogue.ts), so a category whose products are all
  // embroidered arrives empty. Those tabs are dropped rather than shown with an
  // empty grid — the categories are still reachable from the hero slider,
  // featured categories and main navigation.
  const filterOptions = useMemo(() => {
    const options = [{ id: 'all', title: 'All', handle: 'all' }];
    collections
      .filter((collection) => (collection.products?.length ?? 0) > 0)
      .slice(0, 8)
      .forEach((collection) => {
        options.push({
          id: collection.id,
          title: collection.title,
          handle: collection.handle,
        });
      });
    return options;
  }, [collections]);

  // Filter products based on active filter
  const filteredProducts = useMemo(() => {
    if (activeFilter === 'all') {
      return products.slice(0, 10);
    }

    // Find the collection
    const collection = collections.find((c) => c.handle === activeFilter);
    if (collection?.products) {
      return collection.products.slice(0, 10);
    }

    // Fallback for a filter handle with no matching collection. Tabs are built
    // from collections that still have products, so this is unreachable from the
    // homepage; note that its tag test is inert there anyway, because the
    // homepage catalogue ships every card with tags stripped (see
    // src/lib/homepage-catalogue.ts). An empty result falls through to the
    // default slice below.
    return products.filter((product) => {
      const matchesType = product.productType?.toLowerCase().includes(activeFilter.toLowerCase());
      const matchesTags = product.tags?.some((tag) =>
        tag.toLowerCase().includes(activeFilter.toLowerCase())
      );
      return matchesType || matchesTags;
    }).slice(0, 10);
  }, [activeFilter, products, collections]);

  // Use all products if no filtered results
  const displayProducts = filteredProducts.length > 0 ? filteredProducts : products.slice(0, 10);

  return (
    <section className="py-12 lg:py-16">
      <div className="container">
        {/* Title */}
        <h2 className="text-xl lg:text-2xl font-bold text-center uppercase tracking-wider mb-8">
          New And Popular
        </h2>

        {/* Filter Tabs */}
        <div ref={scrollContainerRef} className="flex mb-8 overflow-x-auto pb-2 scrollbar-hide scroll-smooth">
          <div className="flex gap-2 lg:gap-3 mx-auto px-4">
            {filterOptions.map((option) => (
              <button
                key={option.id}
                onClick={(e) => handleFilterClick(option.handle, e.currentTarget)}
                className={`px-4 lg:px-6 py-2 text-xs lg:text-sm font-medium uppercase tracking-wide whitespace-nowrap transition-all duration-200 ${
                  activeFilter === option.handle
                    ? 'bg-brand-blue text-white'
                    : 'bg-white text-black border border-black hover:bg-gray-100'
                }`}
              >
                {option.title}
              </button>
            ))}
          </div>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-6">
          {displayProducts.map((product) => (
            <Link
              key={product.id}
              href={`/products/${product.handle}`}
              className="group"
            >
              {/* Product Image */}
              <div className="relative aspect-[3/4] bg-[var(--color-off-white)] overflow-hidden mb-3">
                {product.featuredImage && (
                  <Image
                    src={product.featuredImage.url}
                    alt={product.featuredImage.altText ?? product.title}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
                  />
                )}

                {/* Wishlist Heart Icon */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleItem(product);
                  }}
                  aria-label={isInWishlist(product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                  className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill={isInWishlist(product.id) ? '#e11d48' : 'none'}
                    stroke={isInWishlist(product.id) ? '#e11d48' : 'currentColor'}
                    strokeWidth={isInWishlist(product.id) ? 0 : 1.5}
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                    />
                  </svg>
                </button>
              </div>

              {/* Product Info */}
              <div>
                <h3 className="text-[13px] font-medium text-gray-800 line-clamp-1 mb-1">
                  {product.title}
                </h3>
                <p className="text-[13px] font-semibold">
                  {formatPrice(product.priceRange.minVariantPrice)}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Hide scrollbar styles */}
      <style jsx global>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
}
